import { rem } from 'polished'
import React, { useCallback, useContext } from 'react'
import styled from 'styled-components'

import TransportItemContent from './TransportItemContent'
import { SAVE_CURATED_TRIP_BUTTON_CLASS } from '../../Buttons/SaveCuratedTripItemButton'
import TripItemSummary from '../../TripModal/TripItems/Summary'
import { ItemCardVariant } from '../types'

import TextButton from 'components/Luxkit/Button/TextButton'
import AssistChip from 'components/Luxkit/Chips/AssistChip'
import LinePaperclipIcon from 'components/Luxkit/Icons/line/LinePaperclipIcon'
import SaveTripItemButton from 'tripPlanner/components/Buttons/SaveTripItemButton'
import AttachedDocument from 'tripPlanner/components/Documents/AttachedDocument'
import BaseItemCard from 'tripPlanner/components/ItemCard/BaseItemCard'
import CardContainer from 'tripPlanner/components/ItemCard/Common/CardContainer'
import TripContext from 'tripPlanner/contexts/TripContext'
import { TripDocumentsContext } from 'tripPlanner/contexts/TripDocumentsContext'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import { TransportItem } from 'tripPlanner/types/tripItem'

interface Props {
  item: TransportItem
  tripNameLabel?: React.ReactNode
  variant: ItemCardVariant
}

const LocalContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
  align-items: baseline;
  justify-content: space-between;
  width: 100%;
  height: 100%;

  & > .${SAVE_CURATED_TRIP_BUTTON_CLASS} {
    align-self: end;
  }
`
function TransportItemCard({ item, tripNameLabel, variant }: Props) {
  const tripContext = useContext(TripContext)
  const canEdit = tripContext?.permissions.canEdit ?? false
  const openModal = useOpenModal()

  const showDetail = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      openModal(TripItemSummary, { item })
      e.stopPropagation()
    },
    [item, openModal],
  )

  const documentContext = useContext(TripDocumentsContext)
  const attachedDocuments =
    documentContext?.documentsResult?.attachedFiles[item.id] ?? []

  return (
    <BaseItemCard item={item}>
      <CardContainer>
        <LocalContainer>
          <TransportItemContent item={item} variant={variant} />
          {attachedDocuments.map((document) => (
            <AttachedDocument key={document.filename} document={document} />
          ))}
          {documentContext && canEdit && (
            <AssistChip
              size="small"
              onClick={() => documentContext.onRequestAddDocument(item.id)}
              variant="filled"
              startIcon={<LinePaperclipIcon />}
            >
              Attach document
            </AssistChip>
          )}
          {tripNameLabel}
          <SaveTripItemButton
            className={SAVE_CURATED_TRIP_BUTTON_CLASS}
            item={item}
          />
          <div>
            <TextButton
              kind="tertiary"
              size="small"
              variant="default"
              outdent="left"
              nonInteractable
              onClick={showDetail}
            >
              View details
            </TextButton>
          </div>
        </LocalContainer>
      </CardContainer>
    </BaseItemCard>
  )
}

export default TransportItemCard
